import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Bell,
  FileDown,
  Mail,
  Loader2,
  RefreshCw,
  AlertTriangle,
  ShieldAlert,
  CheckCircle2,
  Eye,
  Send,
  Clock,
  PlayCircle,
  CalendarClock,
} from "lucide-react";
import { InstallmentPlanDialog } from "@/components/InstallmentPlanDialog";

const LEVEL_META = {
  1: { label: "Zahlungserinnerung", tone: "bg-slate-100 text-slate-800 border-slate-200", icon: Bell },
  2: { label: "1. Mahnung", tone: "bg-amber-100 text-amber-900 border-amber-200", icon: AlertTriangle },
  3: { label: "Letzte Mahnung", tone: "bg-rose-100 text-rose-900 border-rose-200", icon: ShieldAlert },
};

function formatCHF(value, currency = "CHF") {
  const n = Number(value || 0);
  return `${currency} ${n.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function Dunning() {
  const { company } = useCompany();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [processing, setProcessing] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerTriggering, setSchedulerTriggering] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [planCandidate, setPlanCandidate] = useState(null);

  const companyId = company?._id || localStorage.getItem("demoCompanyId");

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/companies/${companyId}/dunning-candidates`);
      setCandidates(res.data || []);
      setSelected(new Set());
    } catch (e) {
      console.error(e);
      toast.error("Fehler beim Laden der Mahnkandidaten");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadSchedulerStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/scheduler/status");
      setSchedulerStatus(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadSchedulerStatus();
  }, [loadSchedulerStatus]);

  const triggerSchedulerNow = async () => {
    setSchedulerTriggering(true);
    try {
      const { data } = await apiClient.post("/scheduler/dunning/run-now");
      toast.success(
        `Batch fertig: ${data.success_count}/${data.invoices_processed} Mahnungen versendet (${data.companies_processed} Firmen)`
      );
      await Promise.all([load(), loadSchedulerStatus()]);
    } catch (e) {
      console.error(e);
      toast.error("Batch-Trigger fehlgeschlagen");
    } finally {
      setSchedulerTriggering(false);
    }
  };

  const togglePick = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.invoice_id)));
  };

  const previewPdf = async (cand) => {
    try {
      const res = await apiClient.get(
        `/invoices/${cand.invoice_id}/dunning-pdf?level=${cand.next_level}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      console.error(e);
      toast.error("Vorschau fehlgeschlagen");
    }
  };

  const sendOne = async (cand, withEmail) => {
    setProcessing(cand.invoice_id);
    try {
      const res = await apiClient.post(`/invoices/${cand.invoice_id}/send-dunning`, {
        level: cand.next_level,
        send_email: withEmail,
      });
      if (res.data.email_sent) {
        toast.success(`Mahnung Stufe ${cand.next_level} versendet an ${cand.customer_email}`);
      } else if (withEmail && res.data.email_error) {
        toast.warning(`Stufe gesetzt, aber E-Mail fehlgeschlagen: ${res.data.email_error}`);
      } else {
        toast.success(`Mahnstufe ${cand.next_level} gesetzt (ohne E-Mail)`);
      }
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Mahnung fehlgeschlagen");
    } finally {
      setProcessing(null);
    }
  };

  const runBatch = async () => {
    if (selected.size === 0 && candidates.length === 0) return;
    setBatchRunning(true);
    try {
      const ids = selected.size > 0 ? Array.from(selected) : candidates.map((c) => c.invoice_id);
      const res = await apiClient.post(`/companies/${companyId}/dunning-run`, {
        invoice_ids: ids,
        send_email: true,
      });
      toast.success(`${res.data.success} von ${res.data.processed} Mahnungen erfolgreich`);
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} Mahnungen fehlgeschlagen (siehe Details)`);
      }
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Batch-Mahnung fehlgeschlagen");
    } finally {
      setBatchRunning(false);
    }
  };

  const totalDue = candidates.reduce((s, c) => s + c.payable_total, 0);

  return (
    <div className="space-y-6" data-testid="dunning-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Mahnwesen</h1>
          <p className="mt-1 text-sm text-slate-500">
            Offene Rechnungen, die zur nächsten Mahnstufe anstehen. Alle Mahnungen werden als
            PDF mit Swiss QR-Zahlteil erstellt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="dunning-refresh-button"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
          <Button
            size="sm"
            className="rounded-full bg-slate-900 text-slate-50"
            disabled={batchRunning || candidates.length === 0}
            onClick={runBatch}
            data-testid="dunning-batch-button"
          >
            {batchRunning ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Sende …
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {selected.size > 0 ? `${selected.size} mahnen` : "Alle mahnen"}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Scheduler-Status */}
      {schedulerStatus && (
        <Card className="p-4 border-slate-200 bg-slate-50/50" data-testid="dunning-scheduler-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-white border border-slate-200">
                <Clock className="h-4 w-4 text-slate-700" />
              </div>
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-slate-900">
                  Automatischer Mahn-Lauf
                  <Badge
                    variant="outline"
                    className={
                      "ml-2 text-[10px] " +
                      (schedulerStatus.running
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-600 border-slate-200")
                    }
                    data-testid="dunning-scheduler-status-badge"
                  >
                    {schedulerStatus.running ? "aktiv" : "inaktiv"}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500">
                  {schedulerStatus.cron_expression}
                  {schedulerStatus.next_run_time && (
                    <>
                      {" · nächster Lauf: "}
                      <span className="font-mono text-slate-700" data-testid="dunning-scheduler-next-run">
                        {new Date(schedulerStatus.next_run_time).toLocaleString("de-CH", {
                          timeZone: "Europe/Zurich",
                        })}
                      </span>
                    </>
                  )}
                </div>
                {schedulerStatus.recent_runs && schedulerStatus.recent_runs.length > 0 && (
                  <div className="text-xs text-slate-500 mt-1">
                    Letzter Lauf:{" "}
                    <span className="text-slate-700">
                      {new Date(schedulerStatus.recent_runs[0].started_at).toLocaleString("de-CH", {
                        timeZone: "Europe/Zurich",
                      })}
                    </span>{" "}
                    · {schedulerStatus.recent_runs[0].success_count}/
                    {schedulerStatus.recent_runs[0].invoices_processed} erfolgreich
                  </div>
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={triggerSchedulerNow}
              disabled={schedulerTriggering}
              data-testid="dunning-scheduler-run-now-button"
              title="Startet den automatischen Mahnlauf sofort, statt auf den nächtlichen Zeitplan zu warten"
            >
              {schedulerTriggering ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Läuft...
                </>
              ) : (
                <>
                  <PlayCircle className="h-3.5 w-3.5 mr-1.5" />
                  Jetzt ausführen
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* Summary */}
      <div className="grid gap-3 md:grid-cols-3" data-testid="dunning-summary">
        <Card className="p-4 bg-white border-slate-200">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Mahnbar</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">{candidates.length}</div>
          <div className="text-xs text-slate-500 mt-0.5">Rechnungen ausstehend</div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Offener Betrag</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">{formatCHF(totalDue, company?.currency)}</div>
          <div className="text-xs text-slate-500 mt-0.5">Inkl. Mahngebühren</div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Höchste Stufe</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">
            {candidates.length === 0 ? "—" : Math.max(...candidates.map((c) => c.next_level))}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Im Pool</div>
        </Card>
      </div>

      {/* List */}
      <Card className="p-0 overflow-hidden" data-testid="dunning-list-card">
        {loading ? (
          <div className="py-12 text-center text-xs text-slate-400">Lade …</div>
        ) : candidates.length === 0 ? (
          <div className="py-16 text-center" data-testid="dunning-empty">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-slate-700">Keine Mahnungen offen. Alle Zahlungen fliessen.</p>
            <p className="text-xs text-slate-400 mt-1">
              Rechnungen erscheinen hier, sobald sie die konfigurierten Tage überfällig sind.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left font-medium text-slate-600 p-3 w-8">
                    <input
                      type="checkbox"
                      checked={selected.size === candidates.length && candidates.length > 0}
                      onChange={toggleAll}
                      data-testid="dunning-select-all"
                      aria-label="Alle auswählen"
                    />
                  </th>
                  <th className="text-left font-medium text-slate-600 p-3">Rechnung</th>
                  <th className="text-left font-medium text-slate-600 p-3">Kunde</th>
                  <th className="text-right font-medium text-slate-600 p-3">Fällig seit</th>
                  <th className="text-left font-medium text-slate-600 p-3">Nächste Stufe</th>
                  <th className="text-right font-medium text-slate-600 p-3">Gebühr</th>
                  <th className="text-right font-medium text-slate-600 p-3">Zins</th>
                  <th className="text-right font-medium text-slate-600 p-3">Zahlbar</th>
                  <th className="text-right font-medium text-slate-600 p-3">Aktion</th>
                </tr>
              </thead>
              <tbody data-testid="dunning-list">
                {candidates.map((c) => {
                  const meta = LEVEL_META[c.next_level] || LEVEL_META[1];
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={c.invoice_id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`dunning-row-${c.invoice_id}`}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selected.has(c.invoice_id)}
                          onChange={() => togglePick(c.invoice_id)}
                          data-testid={`dunning-select-${c.invoice_id}`}
                          aria-label={`Auswählen ${c.invoice_number}`}
                        />
                      </td>
                      <td className="p-3 font-medium text-slate-900">{c.invoice_number}</td>
                      <td className="p-3">
                        <div className="text-slate-900">{c.customer_name || "—"}</div>
                        {c.customer_email && (
                          <div className="text-[11px] text-slate-500">{c.customer_email}</div>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <Badge variant="destructive" className="text-[10px] h-5">
                          {c.days_overdue} Tage
                        </Badge>
                        <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(c.due_date)}</div>
                      </td>
                      <td className="p-3">
                        <Badge className={`${meta.tone} text-[10px] h-5 px-2 border`}>
                          <Icon className="h-3 w-3 mr-1" />
                          Stufe {c.next_level} · {meta.label}
                        </Badge>
                      </td>
                      <td className="p-3 text-right text-slate-900 tabular-nums">
                        {c.next_fee > 0 ? formatCHF(c.next_fee, c.currency) : "—"}
                      </td>
                      <td className="p-3 text-right text-slate-900 tabular-nums" data-testid={`dunning-interest-${c.invoice_id}`}>
                        {c.interest > 0 ? formatCHF(c.interest, c.currency) : "—"}
                      </td>
                      <td className="p-3 text-right font-semibold text-slate-900 tabular-nums">
                        {formatCHF(c.payable_total, c.currency)}
                      </td>
                      <td className="p-3 text-right space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPlanCandidate(c)}
                          data-testid={`dunning-installment-${c.invoice_id}`}
                          title="Ratenzahlung vereinbaren"
                        >
                          <CalendarClock className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => previewPdf(c)}
                          data-testid={`dunning-preview-${c.invoice_id}`}
                          title="Vorschau PDF"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => sendOne(c, false)}
                          disabled={processing === c.invoice_id}
                          data-testid={`dunning-send-silent-${c.invoice_id}`}
                          title="Nur markieren (keine E-Mail)"
                        >
                          <FileDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => sendOne(c, true)}
                          disabled={processing === c.invoice_id}
                          className="bg-slate-900 text-slate-50"
                          data-testid={`dunning-send-${c.invoice_id}`}
                          title="Mahnung per E-Mail senden"
                        >
                          {processing === c.invoice_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Mail className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <InstallmentPlanDialog
        open={!!planCandidate}
        candidate={planCandidate}
        onClose={() => setPlanCandidate(null)}
        onCreated={load}
      />
    </div>
  );
}
